import { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FileUpload } from "primereact/fileupload";
import { Divider } from "primereact/divider";

interface SignatureProps {
  onSignatureChange: (signatureUrl: string | null) => void;
  initialSignature?: string | null;
}

export default function Signature({ onSignatureChange, initialSignature = null }: SignatureProps) {
  const [text, setText] = useState<string>("");
  const [image, setImage] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const drawSignature = () => {
    if (!text.trim() || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 400;
    canvas.height = 150;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = "48px 'Dancing Script', cursive";
    ctx.fillStyle = "black";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const signatureUrl = canvas.toDataURL("image/png");
    setSignature(signatureUrl);
    onSignatureChange(signatureUrl);
    setImage(null);
    setIsEditing(false);
  };

  const handleImageUpload = (event: { files: File[] }) => {
    const file = event.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setSignature(reader.result);
        setImage(reader.result);
        onSignatureChange(reader.result);
        setText("");
        setIsEditing(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearSignature = () => {
    setSignature(null);
    setText("");
    setImage(null);
    onSignatureChange(null);
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  useEffect(() => {
    if (initialSignature) {
      setSignature(initialSignature);
    }
  }, [initialSignature]);

  return (
    <div className="signature">
      {isEditing ? (
        <div>
          <div>
            <div className="p-inputgroup mb-3">
              <InputText
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your name"
                className="p-inputtext-sm PrimeSmallInput"
              />
              <Button
                label="Generate"
                icon="pi pi-pencil"
                size="small"
                style={{ padding: "5px 10px" }}
                onClick={drawSignature}
              />
            </div>

            <Divider align="center">OR</Divider>

            <FileUpload
              mode="basic"
              name="signature"
              accept="image/png, image/jpeg"
              maxFileSize={1000000}
              chooseLabel="Upload Signature"
              uploadLabel="Upload"
              onSelect={handleImageUpload}
              auto
              customUpload
              className="PrimeSmallFileUploadFullWidth"
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-outlined p-button-secondary mt-3"
                size="small"
                style={{ padding: "5px 10px" }}
                onClick={cancelEditing}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="signature-display" style={{ textAlign: "center" }}>
          <div
            className="signature-image-container"
            style={{
              height: "80px",
              width: "250px",
              border: "1px dashed #ccc",
              borderRadius: "4px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: signature ? "transparent" : "#f5f5f5",
              cursor: signature ? "default" : "pointer",
              marginBottom: "10px"
            }}
            onClick={() => !signature && startEditing()}
          >
            {signature ? (
              <img
                src={signature}
                alt="Signature"
                className="signature-image"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : (
              <span className="text-500">Click to add signature</span>
            )}
          </div>

          <div className="signature-actions" style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
            {signature && (
              <>
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  className="p-button-outlined"
                  size="small"
                  style={{ padding: "5px 10px" }}
                  onClick={startEditing}
                />
                <Button
                  label="Clear"
                  icon="pi pi-trash"
                  className="p-button-outlined p-button-danger"
                  size="small"
                  style={{ padding: "5px 10px" }}
                  onClick={clearSignature}
                />
              </>
            )}

          </div>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}